import _ from "lodash";
import { LanguageCode } from "iso-639-1";
import { all } from "iso-3166-1";

type CountryAlpha2 = ReturnType<typeof all>[number]["alpha2"];

export type Language = LanguageCode | `${LanguageCode}-${CountryAlpha2}`;

type JamezzLanguageDefinition = {
  value: Language;
  label: string;
  transKey: string;
  flag?: string;
};

/**
 * List of all languages used spread out over all Jamezz components.
 * We have three different language sources
 * - talenV5, languages supported in V5 app and kiosk
 * - backofficeLanguages: languages for the backoffice
 * - selectableKioskV2Languages: languages for kioskv2
 */
export const talenV5: { [key in Language]?: JamezzLanguageDefinition } = {
  ar: {
    value: "ar",
    label: "اَلْعَرَبِيَّةُ" + " - Arabic",
    transKey: "app.talen.arabic",
    flag: "/img/flag_nl_big.png",
  },
  nl: {
    value: "nl",
    label: "Nederlands - Dutch",
    transKey: "app.talen.nederlands",
    flag: "/img/flag_nl_big.png",
  },
  "nl-BE": {
    value: "nl-BE",
    label: "Nederlands - Dutch - Belgium",
    transKey: "app.talen.nederlands",
    flag: "/img/flag_nl_big.png",
  },
  en: {
    value: "en",
    label: "English",
    transKey: "app.talen.engels",
    flag: "/img/flag_en_big.png",
  },
  fr: {
    value: "fr",
    label: "Français - French",
    transKey: "app.talen.frans",
    flag: "/img/flag_fr_big.png",
  },
  de: {
    value: "de",
    label: "Deutsch - German",
    transKey: "app.talen.duits",
    flag: "/img/flag_de_big.png",
  },
  da: {
    value: "da",
    label: "Dansk - Danish",
    transKey: "app.talen.deens",
    flag: "/img/flag_da_big.png",
  },
  sv: {
    value: "sv",
    label: "Svenska - Swedish",
    transKey: "app.talen.zweeds",
    flag: "/img/flag_sv_big.png",
  },
  es: {
    value: "es",
    label: "Espaǹol - Spanish",
    transKey: "app.talen.spaans",
    flag: "/img/flag_es_big.png",
  },
  it: { value: "it", label: "Italiano - Italian", transKey: "app.talen.italiaans", flag: "/img/flag_it_big.png" },
  ja: { value: "ja", label: "日本語 - Japanese", transKey: "app.talen.japans.v5", flag: "/img/flag_jp_big.png" },
  zh: { value: "zh", label: "普通话 - Chinese", transKey: "app.talen.chinees.v5", flag: "/img/flag_cn_big.png" },
  hr: { value: "hr", label: "Hrvatski - Croatian", transKey: "app.talen.kroatisch", flag: "/img/flag_hr_big.png" },
  ca: { value: "ca", label: "Català - Catalan", transKey: "app.talen.catalan", flag: "/img/flag_es-ct_big.png" },
  uk: { value: "uk", label: "Україна - Ukraine", transKey: "app.talen.ukraine", flag: "/img/flag_uk-ct_big.png" },
  pl: { value: "pl", label: "Polski - Polish", transKey: "app.talen.polish", flag: "/img/flag_pl-ct_big.png" },
};

export type JamezzLanguage = keyof typeof talenV5;

/**
 * Backoffice languages
 */
export const backofficeLanguages = _.pick(talenV5, ["nl", "en", "fr", "de", "es"]);
/**
 * V2/V3 languages omit Italian and Catalan
 */
export const selectableKioskV2Languages = _.omit(talenV5, ["it", "ca"]);

/**
 * Lijst met beschikbare placeholders voor berichten
 */
export const placeholders = ["slotName", "externOrderID", "externTransID", "tafelNr"];

/**
 * Lijst met beschikbare printermodes
 */
export const printerModes = [
  { value: "", label: "geen default printer", transKey: "geen default printer" },
  { value: "all", label: "Alles", transKey: "app.printerModes.all" },
  { value: "food", label: "Eten", transKey: "app.printerModes.food" },
  { value: "drinks", label: "Drinken", transKey: "app.printerModes.drinks" },
];

/**
 * Auto open menu mobile
 */
export const autoOpenMode = [
  { value: "0", label: "None", transKey: "app.autoOpenMode.all" },
  { value: "1", label: "Food", transKey: "app.autoOpenMode.food" },
  { value: "2", label: "Drinks", transKey: "app.autoOpenMode.drinks" },
];

/**
 * Product sort mode
 */
export const sortModes = [
  { value: 0, label: "Default", transKey: "app.sortMode.Default" },
  { value: 1, label: "Alfabetical", transKey: "app.sortMode.Alfabetical" },
];

/**
 * Service app view
 */
export const serviceAppView = [
  { value: 0, label: "Disabled", transKey: "app.serviceAppView.disabled" },
  { value: 1, label: "Kitchen Display Screen", transKey: "app.serviceAppView.kds" },
  { value: 2, label: "Kiosk", transKey: "app.serviceAppView.kiosk" },
  { value: 3, label: "Return URL", transKey: "app.serviceAppView.returnurl" },
];

/**
 * Lijst met printers wordt gebruikt bij producten
 */
export const printers = [{ value: "0", label: "Standaard", transKey: "app.printers.0" }];

/**
 * Lijst met beschikbare menu style wordt gebruik aan de app om de indeling te verandern
 *
 */
export const menuStylen = [
  { value: "", label: "Default", transKey: "app.menuStylen.default" },
  { value: 0, label: "Standaard, producten worden weergegeven in een lijst", transKey: "app.menuStylen.0" },
  { value: 2, label: "Prijs onder, lijst weergave met de prijs onder de omschrijving", transKey: "app.menuStylen.2" },
  { value: 1, label: "Tegels, producten worden weergegeven in tegels (3 kolomen)", transKey: "app.menuStylen.1" },
  { value: 3, label: "Tegels, producten worden weergegeven in tegels (4 kolomen)", transKey: "app.menuStylen.3" },
  {
    value: 5,
    label: "Tegels, zuil v3 producten worden weergegeven in tegels (3 kolomen)",
    transKey: "app.menuStylen.5",
  },
  {
    value: 6,
    label: "Tegels, zuil v3 producten worden weergegeven in tegels (4 kolomen)",
    transKey: "app.menuStylen.6",
  },
  { value: 7, label: "Tegels, zuil v3 producten prijs beneden", transKey: "app.menuStylen.7" },
  { value: 8, label: "Tegels, webshop view", transKey: "app.menuStylen.8" },
  { value: 9, label: "Lijst, webshop view", transKey: "app.menuStylen.9" },
  {
    value: 4,
    label: "Tegels, producten worden weergegeven in tegels extra groot (2 kolomen)",
    transKey: "app.menuStylen.4",
  },
  { value: 10, label: "Tegels, producten worden weergegeven voor mobiel (2 kolomen)", transKey: "app.menuStylen.10" },
  { value: 11, label: "Tegels, zuil nieuw design", transKey: "app.menuStylen.11" },
  { value: 12, label: "Lijst, app met header", transKey: "app.menuStylen.12" },
  { value: 13, label: "Tegels, webshop met header", transKey: "app.menuStylen.13" },
  { value: 14, label: "Kiosk Sushito Style (2 producten naast elkaar)", transKey: "app.menuStylen.14" },
  { value: 15, label: "Kiiro Producten", transKey: "app.menuStylen.15" },
  { value: 16, label: "Kiiro Producten center", transKey: "app.menuStylen.16" },
  { value: 17, label: "Tegels, zuil nieuw design 3", transKey: "app.menuStylen.17" },
  { value: 18, label: "Tegels, zuil nieuw design 4", transKey: "app.menuStylen.18" },
  { value: 19, label: "Webshop large images and info", transKey: "app.menuStylen.19" },
];

/**
 * Lijst met beschikbare kaart style wordt gebruik aan de app om de indeling te verandern
 *
 */
export const kaartStylen = [
  { value: 0, label: "Standaard, kaarten worden weergegeven in een lijst", transKey: "app.kaartStylen.0" },
  { value: 1, label: "Kaart in lijst met een achtergrondafbeelding", transKey: "app.kaartStylen.1" },
  { value: 2, label: "Kaart in lijst inclusief producten", transKey: "app.kaartStylen.2" },
];

/**
 * Active directory mode op tafel
 *
 */
export const adModeTable = [
  { value: 0, label: "Disabled", transKey: "app.adMode.0" },
  { value: 1, label: "PreOrder AD queue", transKey: "app.adMode.1" },
  { value: 2, label: "PreOrder AD to table from queue", transKey: "app.adMode.2" },
  { value: 3, label: "PreOrder / from queue to table", transKey: "app.adMode.3" },
];

/**
 * Video type
 *
 */
export const videoTypeList = [
  { value: 0, label: "Upload image", transKey: "Upload image" },
  { value: 1, label: "YouTube", transKey: "app.videoType.1" },
  { value: 2, label: "Facebook", transKey: "app.videoType.2" },
  { value: 3, label: "SoundCloud ", transKey: "app.videoType.3" },
  { value: 4, label: "Streamable", transKey: "app.videoType.4" },
  { value: 5, label: "Vidme", transKey: "app.videoType.5" },
  { value: 6, label: "Vimeo", transKey: "app.videoType.6" },
  { value: 7, label: "Wistia", transKey: "app.videoType.7" },
  { value: 8, label: "Twitch", transKey: "app.videoType.8" },
  { value: 9, label: "DailyMotion", transKey: "app.videoType.9" },
  { value: 10, label: "Vidyard", transKey: "app.videoType.10" },
  { value: 11, label: "Kaltura's", transKey: "app.videoType.11" },
  { value: 12, label: "General video", transKey: "app.videoType.12" },
];

/**
 * Lijst met mastercodes
 */
export const masterCodeTypes = [
  { value: 0, label: "(0) All you can eat login (max 2 arrangements)", transKey: "app.masterCodeTypes.0" },
  { value: 1, label: "(1) Select table number (selection list)", transKey: "app.masterCodeTypes.1" },
  { value: 2, label: "(2) All you can eat number of persons", transKey: "app.masterCodeTypes.2" },
  { value: 3, label: "(3) All you can eat login (max 4 arrangements)", transKey: "app.masterCodeTypes.3" },
  { value: 4, label: "(4) Select table number all you can eat (selection list)", transKey: "app.masterCodeTypes.4" },
  { value: 97, label: "(97) Choose pickup or delivery (dutch)", transKey: "app.masterCodeTypes.97" },
  { value: 98, label: "(98) Kiosk sitin or takeway selection (dutch)", transKey: "app.masterCodeTypes.98" },
  { value: 99, label: "(99) Order selection Beekse Bergen (breakfast/pizza)", transKey: "app.masterCodeTypes.99" },
  { value: 100, label: "(100) Social Deal / Normal selection", transKey: "app.masterCodeTypes.100" },
  { value: 101, label: "(101) Anne&Max delivery location search", transKey: "app.masterCodeTypes.101" },
  { value: 102, label: "(102) LePoutre location selection", transKey: "app.masterCodeTypes.102" },
  { value: 103, label: "(103) Jamezz postalcode check page (dutch)", transKey: "app.masterCodeTypes.103" },
  { value: 104, label: "(104) Eat in / takeaway select anytyme", transKey: "app.masterCodeTypes.104" },
  { value: 105, label: "(105) Pickup order v3", transKey: "app.masterCodeTypes.105" },
  { value: 106, label: "(106) Frontpage order select het Houtsemeer", transKey: "app.masterCodeTypes.106" },
  { value: 107, label: "(107) Anne&Max pickup location selection", transKey: "app.masterCodeTypes.107" },
  { value: 108, label: "(108) General takeaway/pickup selection page (dutch)", transKey: "app.masterCodeTypes.108" },
  { value: 109, label: "(109) Kiiro delivery page postalcheck", transKey: "app.masterCodeTypes.109" },
  { value: 110, label: "(110) Frontpage order select het H32", transKey: "app.masterCodeTypes.110" },
  { value: 111, label: "(111) Frontpage order select het O Leary's", transKey: "app.masterCodeTypes.111" },
  { value: 112, label: "(112) Apenheul voorpagina", transKey: "app.masterCodeTypes.112" },
  { value: 113, label: "(113) AnyTyme voorpagina Emeloord", transKey: "app.masterCodeTypes.113" },
  { value: 114, label: "(114) Postcode login check google", transKey: "app.masterCodeTypes.114" },
  { value: 115, label: "(115) Binnen opeten meenemen", transKey: "app.masterCodeTypes.115" },
  { value: 116, label: "(116) Landgoed de berenkuil", transKey: "app.masterCodeTypes.116" },
  { value: 117, label: "(117) Afhalen - bezorgen zelfde tafel", transKey: "app.masterCodeTypes.117" },
  { value: 118, label: "(118) Roompot Accomodaties Pizza-Friet keuze", transKey: "app.masterCodeTypes.118" },
  { value: 119, label: "(119) Table select by inputfield (apiId1)", transKey: "app.masterCodeTypes.119" },
  { value: 120, label: "(120) Postal code login check google (EN)", transKey: "app.masterCodeTypes.120" },
  { value: 121, label: "(121) Pickup / Delivery (EN)", transKey: "app.masterCodeTypes.121" },
  { value: 122, label: "(122) Postal code login check google (EN/Paolozzi)", transKey: "app.masterCodeTypes.122" },
  { value: 123, label: "(123) Pickup / Delivery (EN/Paolozzi)", transKey: "app.masterCodeTypes.123" },
  { value: 124, label: "(124) Postcodecheck Harvest", transKey: "app.masterCodeTypes.124" },
  { value: 125, label: "(125) Postal code login check google (EN, customizable)", transKey: "app.masterCodeTypes.125" },
  { value: 126, label: "(126) Pickup, Delivery, SitIn, etc. (customizable)", transKey: "app.masterCodeTypes.126" },
  { value: 127, label: "(127) Scherm select by inputfield", transKey: "app.masterCodeTypes.127" },
  { value: 128, label: "(128) Europarcs broodservice", transKey: "app.masterCodeTypes.128" },
  { value: 129, label: "(129) sessionLoginTakeawaySelect Du Sanglier", transKey: "app.masterCodeTypes.129" },
  { value: 130, label: "(130) All you can eat table/customercount", transKey: "app.masterCodeTypes.130" },
  { value: 131, label: "(131) Scherm select by inputfield met NumPad", transKey: "app.masterCodeTypes.131sche" },
  {
    value: 132,
    label: "(132) Scherm select by inputfield met NumPad (O'Learys)",
    transKey: "app.masterCodeTypes.132leary",
  },
  { value: 133, label: "(133) Haagens Kiosk", transKey: "app.masterCodeTypes.133Haagens" },
  { value: 134, label: "(134) Postal code login check google (DE, customizable)", transKey: "app.masterCodeTypes.134" },
  { value: 135, label: "(135) Roompot Accomodaties Restaurant-Cafetaria keuze", transKey: "app.masterCodeTypes.135" },
  { value: 136, label: "(136) Kamernummer select input", transKey: "app.masterCodeTypes.136" },
  { value: 137, label: "(137) Europarcs takeaway/delivery", transKey: "app.masterCodeTypes.137" },
  { value: 138, label: "(138) Hotels van Oranje roomservice/takeaway", transKey: "app.masterCodeTypes.138" },
  {
    value: 139,
    label: "(139) All you can eat customer login (max 2 arrangements)",
    transKey: "app.masterCodeTypes.139",
  },
  { value: 141, label: "(141) Careflex eatin/takeaway", transKey: "app.masterCodeTypes.141" },
  { value: 142, label: "(142) Roompot Accomodaties Accomodaties- keuze", transKey: "app.masterCodeTypes.142" },
  { value: 143, label: "(143) Bar Balancer", transKey: "app.masterCodeTypes.143" },
  {
    value: 144,
    label: "(144) Roompot Accomodaties Accomodaties- Pizza/Cafetaria",
    transKey: "app.masterCodeTypes.144",
  },
  {
    value: 145,
    label: "(145) Table select by inputfield (apiId3)(Nr from Tablename)",
    transKey: "app.masterCodeTypes.145",
  },
  {
    value: 146,
    label: "(146) CPT Drinks or Food selection",
    transKey: "app.masterCodeTypes.146",
  },
  {
    value: 147,
    label: "(147) Schurter kostenstelle",
    transKey: "app.masterCodeTypes.147",
  },
  {
    value: 148,
    label: "(148) All you can eat login with system settings (max 4 arrangements)",
    transKey: "app.masterCodeTypes.148",
  },
  { value: 149, label: "(149) Postal code login check google (NL, customizable)", transKey: "app.masterCodeTypes.149" },
  { value: 150, label: "(150) Roompot pizza, cafetaria, supermarkt", transKey: "app.masterCodeTypes.150" },
  { value: 151, label: "(151) Room selection list", transKey: "app.masterCodeTypes.151" },
  {
    value: 152,
    label: "(152) Table select by inputfield (apiId1) (Custom background without logo)",
    transKey: "app.masterCodeTypes.152",
  },
  { value: 153, label: "(153) Sky Lounge: selection list (& password)", transKey: "app.masterCodeTypes.153" },
  { value: 154, label: "(154) Sky Lounge: direct (password only)", transKey: "app.masterCodeTypes.154" },
  { value: 155, label: "(155) Choose pickup or delivery (dutch | white button)", transKey: "app.masterCodeTypes.155" },
  { value: 156, label: "(156) Azure AD Login Mastercode", transKey: "app.masterCodeTypes.156" },

  { value: 993, label: "(993) Unlimted Label - 1", transKey: "app.masterCodeTypes.993" },
  { value: 994, label: "(994) Unlimted Label - 2", transKey: "app.masterCodeTypes.994" },
  { value: 995, label: "(995) Unlimted Label - 3", transKey: "app.masterCodeTypes.995" },
  { value: 996, label: "(996) Zuil Lightspeed", transKey: "app.masterCodeTypes.996" },
  { value: 997, label: "(997) Zuil MPlusKassa", transKey: "app.masterCodeTypes.997" },
  { value: 998, label: "(998) sessionLoginTakeawaySelect beide opties tijdelijk", transKey: "app.masterCodeTypes.998" },
  { value: 999, label: "(999) sessionLoginTakeawaySelect", transKey: "app.masterCodeTypes.999" },
  { value: 1000, label: "(1000) sessionLoginTakeawaySelect meenemen", transKey: "app.masterCodeTypes.1000" },
  { value: 2000, label: "(2000) Salesarea selection (via settings)", transKey: "app.masterCodeTypes.2000" },
  {
    value: 2001,
    label: "(2001) 2000-Slave: Uses settings of table with mastercode 2000 (in same sales area).",
    transKey: "app.masterCodeTypes.2001",
  },
  {
    value: 2002,
    label: "(2002) 2000-Slave: Uses 2002-settings of table with mastercode 2000 (in same sales area).",
    transKey: "app.masterCodeTypes.2002",
  },
  {
    value: 2500,
    label: "(2500) Pick up and Delivery (Dutch)",
    transKey: "app.pickup_and_delivery.2500",
  },
];

/**
 * Lijst met wachttijd opties
 */
export const waitTimeMode = [
  { value: 0, label: "Uitgeschakeld, geen wachttijden weergeven", transKey: "app.waitTimeMode.0" },
  {
    value: 1,
    label: "Wachttijd van vestiging is leidend tenzijn product bereiding langer duurt dan de wachttijd",
    transKey: "app.waitTimeMode.1",
  },
  { value: 3, label: "Wachttijd van vestiging + wachttijd van product", transKey: "app.waitTimeMode.3" },
];

export const timeZones = [
  // PHP 7.3
  "Africa/Abidjan",
  "Africa/Accra",
  "Africa/Addis_Ababa",
  "Africa/Algiers",
  "Africa/Asmara",
  "Africa/Bamako",
  "Africa/Bangui",
  "Africa/Banjul",
  "Africa/Bissau",
  "Africa/Blantyre",
  "Africa/Brazzaville",
  "Africa/Bujumbura",
  "Africa/Cairo",
  "Africa/Casablanca",
  "Africa/Ceuta",
  "Africa/Conakry",
  "Africa/Dakar",
  "Africa/Dar_es_Salaam",
  "Africa/Djibouti",
  "Africa/Douala",
  "Africa/El_Aaiun",
  "Africa/Freetown",
  "Africa/Gaborone",
  "Africa/Harare",
  "Africa/Johannesburg",
  "Africa/Juba",
  "Africa/Kampala",
  "Africa/Khartoum",
  "Africa/Kigali",
  "Africa/Kinshasa",
  "Africa/Lagos",
  "Africa/Libreville",
  "Africa/Lome",
  "Africa/Luanda",
  "Africa/Lubumbashi",
  "Africa/Lusaka",
  "Africa/Malabo",
  "Africa/Maputo",
  "Africa/Maseru",
  "Africa/Mbabane",
  "Africa/Mogadishu",
  "Africa/Monrovia",
  "Africa/Nairobi",
  "Africa/Ndjamena",
  "Africa/Niamey",
  "Africa/Nouakchott",
  "Africa/Ouagadougou",
  "Africa/Porto-Novo",
  "Africa/Sao_Tome",
  "Africa/Tripoli",
  "Africa/Tunis",
  "Africa/Windhoek",
  "America/Adak",
  "America/Anchorage",
  "America/Anguilla",
  "America/Antigua",
  "America/Araguaina",
  "America/Argentina/Buenos_Aires",
  "America/Argentina/Catamarca",
  "America/Argentina/Cordoba",
  "America/Argentina/Jujuy",
  "America/Argentina/La_Rioja",
  "America/Argentina/Mendoza",
  "America/Argentina/Rio_Gallegos",
  "America/Argentina/Salta",
  "America/Argentina/San_Juan",
  "America/Argentina/San_Luis",
  "America/Argentina/Tucuman",
  "America/Argentina/Ushuaia",
  "America/Aruba",
  "America/Asuncion",
  "America/Atikokan",
  "America/Bahia",
  "America/Bahia_Banderas",
  "America/Barbados",
  "America/Belem",
  "America/Belize",
  "America/Blanc-Sablon",
  "America/Boa_Vista",
  "America/Bogota",
  "America/Boise",
  "America/Cambridge_Bay",
  "America/Campo_Grande",
  "America/Cancun",
  "America/Caracas",
  "America/Cayenne",
  "America/Cayman",
  "America/Chicago",
  "America/Chihuahua",
  "America/Costa_Rica",
  "America/Creston",
  "America/Cuiaba",
  "America/Curacao",
  "America/Danmarkshavn",
  "America/Dawson",
  "America/Dawson_Creek",
  "America/Denver",
  "America/Detroit",
  "America/Dominica",
  "America/Edmonton",
  "America/Eirunepe",
  "America/El_Salvador",
  "America/Fort_Nelson",
  "America/Fortaleza",
  "America/Glace_Bay",
  "America/Goose_Bay",
  "America/Grand_Turk",
  "America/Grenada",
  "America/Guadeloupe",
  "America/Guatemala",
  "America/Guayaquil",
  "America/Guyana",
  "America/Halifax",
  "America/Havana",
  "America/Hermosillo",
  "America/Indiana/Indianapolis",
  "America/Indiana/Knox",
  "America/Indiana/Marengo",
  "America/Indiana/Petersburg",
  "America/Indiana/Tell_City",
  "America/Indiana/Vevay",
  "America/Indiana/Vincennes",
  "America/Indiana/Winamac",
  "America/Inuvik",
  "America/Iqaluit",
  "America/Jamaica",
  "America/Juneau",
  "America/Kentucky/Louisville",
  "America/Kentucky/Monticello",
  "America/Kralendijk",
  "America/La_Paz",
  "America/Lima",
  "America/Los_Angeles",
  "America/Lower_Princes",
  "America/Maceio",
  "America/Managua",
  "America/Manaus",
  "America/Marigot",
  "America/Martinique",
  "America/Matamoros",
  "America/Mazatlan",
  "America/Menominee",
  "America/Merida",
  "America/Metlakatla",
  "America/Mexico_City",
  "America/Miquelon",
  "America/Moncton",
  "America/Monterrey",
  "America/Montevideo",
  "America/Montserrat",
  "America/Nassau",
  "America/New_York",
  "America/Nipigon",
  "America/Nome",
  "America/Noronha",
  "America/North_Dakota/Beulah",
  "America/North_Dakota/Center",
  "America/North_Dakota/New_Salem",
  "America/Nuuk",
  "America/Ojinaga",
  "America/Panama",
  "America/Pangnirtung",
  "America/Paramaribo",
  "America/Phoenix",
  "America/Port-au-Prince",
  "America/Port_of_Spain",
  "America/Porto_Velho",
  "America/Puerto_Rico",
  "America/Punta_Arenas",
  "America/Rainy_River",
  "America/Rankin_Inlet",
  "America/Recife",
  "America/Regina",
  "America/Resolute",
  "America/Rio_Branco",
  "America/Santarem",
  "America/Santiago",
  "America/Santo_Domingo",
  "America/Sao_Paulo",
  "America/Scoresbysund",
  "America/Sitka",
  "America/St_Barthelemy",
  "America/St_Johns",
  "America/St_Kitts",
  "America/St_Lucia",
  "America/St_Thomas",
  "America/St_Vincent",
  "America/Swift_Current",
  "America/Tegucigalpa",
  "America/Thule",
  "America/Thunder_Bay",
  "America/Tijuana",
  "America/Toronto",
  "America/Tortola",
  "America/Vancouver",
  "America/Whitehorse",
  "America/Winnipeg",
  "America/Yakutat",
  "America/Yellowknife",
  "Antarctica/Casey",
  "Antarctica/Davis",
  "Antarctica/DumontDUrville",
  "Antarctica/Macquarie",
  "Antarctica/Mawson",
  "Antarctica/McMurdo",
  "Antarctica/Palmer",
  "Antarctica/Rothera",
  "Antarctica/Syowa",
  "Antarctica/Troll",
  "Antarctica/Vostok",
  "Arctic/Longyearbyen",
  "Asia/Aden",
  "Asia/Almaty",
  "Asia/Amman",
  "Asia/Anadyr",
  "Asia/Aqtau",
  "Asia/Aqtobe",
  "Asia/Ashgabat",
  "Asia/Atyrau",
  "Asia/Baghdad",
  "Asia/Bahrain",
  "Asia/Baku",
  "Asia/Bangkok",
  "Asia/Barnaul",
  "Asia/Beirut",
  "Asia/Bishkek",
  "Asia/Brunei",
  "Asia/Chita",
  "Asia/Choibalsan",
  "Asia/Colombo",
  "Asia/Damascus",
  "Asia/Dhaka",
  "Asia/Dili",
  "Asia/Dubai",
  "Asia/Dushanbe",
  "Asia/Famagusta",
  "Asia/Gaza",
  "Asia/Hebron",
  "Asia/Ho_Chi_Minh",
  "Asia/Hong_Kong",
  "Asia/Hovd",
  "Asia/Irkutsk",
  "Asia/Jakarta",
  "Asia/Jayapura",
  "Asia/Jerusalem",
  "Asia/Kabul",
  "Asia/Kamchatka",
  "Asia/Karachi",
  "Asia/Kathmandu",
  "Asia/Khandyga",
  "Asia/Kolkata",
  "Asia/Krasnoyarsk",
  "Asia/Kuala_Lumpur",
  "Asia/Kuching",
  "Asia/Kuwait",
  "Asia/Macau",
  "Asia/Magadan",
  "Asia/Makassar",
  "Asia/Manila",
  "Asia/Muscat",
  "Asia/Nicosia",
  "Asia/Novokuznetsk",
  "Asia/Novosibirsk",
  "Asia/Omsk",
  "Asia/Oral",
  "Asia/Phnom_Penh",
  "Asia/Pontianak",
  "Asia/Pyongyang",
  "Asia/Qatar",
  "Asia/Qostanay",
  "Asia/Qyzylorda",
  "Asia/Riyadh",
  "Asia/Sakhalin",
  "Asia/Samarkand",
  "Asia/Seoul",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Srednekolymsk",
  "Asia/Taipei",
  "Asia/Tashkent",
  "Asia/Tbilisi",
  "Asia/Tehran",
  "Asia/Thimphu",
  "Asia/Tokyo",
  "Asia/Tomsk",
  "Asia/Ulaanbaatar",
  "Asia/Urumqi",
  "Asia/Ust-Nera",
  "Asia/Vientiane",
  "Asia/Vladivostok",
  "Asia/Yakutsk",
  "Asia/Yangon",
  "Asia/Yekaterinburg",
  "Asia/Yerevan",
  "Atlantic/Azores",
  "Atlantic/Bermuda",
  "Atlantic/Canary",
  "Atlantic/Cape_Verde",
  "Atlantic/Faroe",
  "Atlantic/Madeira",
  "Atlantic/Reykjavik",
  "Atlantic/South_Georgia",
  "Atlantic/St_Helena",
  "Atlantic/Stanley",
  "Australia/Adelaide",
  "Australia/Brisbane",
  "Australia/Broken_Hill",
  "Australia/Currie",
  "Australia/Darwin",
  "Australia/Eucla",
  "Australia/Hobart",
  "Australia/Lindeman",
  "Australia/Lord_Howe",
  "Australia/Melbourne",
  "Australia/Perth",
  "Australia/Sydney",
  "Europe/Amsterdam",
  "Europe/Andorra",
  "Europe/Astrakhan",
  "Europe/Athens",
  "Europe/Belgrade",
  "Europe/Berlin",
  "Europe/Bratislava",
  "Europe/Brussels",
  "Europe/Bucharest",
  "Europe/Budapest",
  "Europe/Busingen",
  "Europe/Chisinau",
  "Europe/Copenhagen",
  "Europe/Dublin",
  "Europe/Gibraltar",
  "Europe/Guernsey",
  "Europe/Helsinki",
  "Europe/Isle_of_Man",
  "Europe/Istanbul",
  "Europe/Jersey",
  "Europe/Kaliningrad",
  "Europe/Kiev",
  "Europe/Kirov",
  "Europe/Lisbon",
  "Europe/Ljubljana",
  "Europe/London",
  "Europe/Luxembourg",
  "Europe/Madrid",
  "Europe/Malta",
  "Europe/Mariehamn",
  "Europe/Minsk",
  "Europe/Monaco",
  "Europe/Moscow",
  "Europe/Oslo",
  "Europe/Paris",
  "Europe/Podgorica",
  "Europe/Prague",
  "Europe/Riga",
  "Europe/Rome",
  "Europe/Samara",
  "Europe/San_Marino",
  "Europe/Sarajevo",
  "Europe/Saratov",
  "Europe/Simferopol",
  "Europe/Skopje",
  "Europe/Sofia",
  "Europe/Stockholm",
  "Europe/Tallinn",
  "Europe/Tirane",
  "Europe/Ulyanovsk",
  "Europe/Uzhgorod",
  "Europe/Vaduz",
  "Europe/Vatican",
  "Europe/Vienna",
  "Europe/Vilnius",
  "Europe/Volgograd",
  "Europe/Warsaw",
  "Europe/Zagreb",
  "Europe/Zaporozhye",
  "Europe/Zurich",
  "Indian/Antananarivo",
  "Indian/Chagos",
  "Indian/Christmas",
  "Indian/Cocos",
  "Indian/Comoro",
  "Indian/Kerguelen",
  "Indian/Mahe",
  "Indian/Maldives",
  "Indian/Mauritius",
  "Indian/Mayotte",
  "Indian/Reunion",
  "Pacific/Apia",
  "Pacific/Auckland",
  "Pacific/Bougainville",
  "Pacific/Chatham",
  "Pacific/Chuuk",
  "Pacific/Easter",
  "Pacific/Efate",
  "Pacific/Enderbury",
  "Pacific/Fakaofo",
  "Pacific/Fiji",
  "Pacific/Funafuti",
  "Pacific/Galapagos",
  "Pacific/Gambier",
  "Pacific/Guadalcanal",
  "Pacific/Guam",
  "Pacific/Honolulu",
  "Pacific/Kiritimati",
  "Pacific/Kosrae",
  "Pacific/Kwajalein",
  "Pacific/Majuro",
  "Pacific/Marquesas",
  "Pacific/Midway",
  "Pacific/Nauru",
  "Pacific/Niue",
  "Pacific/Norfolk",
  "Pacific/Noumea",
  "Pacific/Pago_Pago",
  "Pacific/Palau",
  "Pacific/Pitcairn",
  "Pacific/Pohnpei",
  "Pacific/Port_Moresby",
  "Pacific/Rarotonga",
  "Pacific/Saipan",
  "Pacific/Tahiti",
  "Pacific/Tarawa",
  "Pacific/Tongatapu",
  "Pacific/Wake",
  "Pacific/Wallis",
  "UTC",
];

/**
 * Lijst met keuken button layouts
 */
export const kitchenScreenButtonLayout = [
  { value: 0, label: "Standaard, alles", transKey: "app.kitchenScreenButtonLayout.0" },
  { value: 1, label: "Alleen gereed/ready", transKey: "app.kitchenScreenButtonLayout.1" },
];

export const kitchenScreenBlockedButtons = [
  { value: 0, label: "Default, disable button only", transKey: "app.kitchenBlockedButtons.0" },
  { value: 1, label: "Suspend button only", transKey: "app.kitchenBlockedButtons.1" },
  { value: 2, label: "Both block and suspend buttons", transKey: "app.kitchenBlockedButtons.1" },
];

/**
 * Lijst met api providers
 */

/**
 * Lijst met pay provider
 */
export const payProvider = [
  { value: "PAYNL", label: "Pay NL", secundair: false },
  { value: "CM", label: "Jamezz Payments (CM)", secundair: false },
  { value: "ADYEN", label: "Adyen", secundair: false },
  { value: "ADYENPLATFORMS", label: "Adyen platforms", secundair: false },
  { value: "MOLLIE", label: "Jamezz Payments (Mollie)", secundair: false },
  { value: "CCV", label: "CCV", secundair: true, secundairValue: "Ccv" },
  { value: "WORLDLINE", label: "Worldline", secundair: false },
  { value: "STRIPE", label: "Stripe", secundair: false },
  { value: "SPLITT", label: "Splitt", secundair: false },
  { value: "TWOCHECKOUT", label: "2 Checkout", secundair: false },
  { value: "CIKAM", label: "Cikam", secundair: true, secundairValue: "Cikam" },
  { value: "SAFERPAY", label: "Saferpay", secundair: false },
  { value: "EMSPAYMENT", label: "Ems Payment", secundair: false },
  { value: "PAYMENTSENSE", label: "Payment sense", secundair: false },
  { value: "SIXPAYMENTSTIM", label: "Six Payments TIM", secundair: false },
  { value: "OMNIKASSA", label: "Omni Kassa", secundair: false },
  { value: "PAGADIRECT", label: "Paga Direct", secundair: false },
  { value: "GIFTY", label: "Gifty", secundair: true, secundairValue: "Gifty" },
  { value: "PIGGY", label: "Piggy", secundair: true, secundairValue: "Piggy" },
  { value: "WESTPAY", label: "Westpay", secundair: false },
  { value: "BAC", label: "Brussel Airport Service", secundair: false },
  { value: "VERIFONE_FR", label: "Verifone Card Terminal (FR)", secundair: false },
  { value: "DOJO", label: "Dojo (Payment Sense) Card Terminal (GB)", secundair: false },
  { value: "DOJO-ECOMMERCE", label: "Dojo E-Commerce (GB)", secundair: false },
  { value: "ZVT", label: "ZVT Protocol (Germany, Austria, Switzerland)", secundair: false },
  { value: "TEST", label: "Test payments", secundair: false },
];

/**
 * Food Icons
 */
export const foodIcons = [
  { value: "", label: "", transKey: "" },
  { value: "faAppleAlt", label: "Apple", transKey: "app.foodIconNames.faAppleAlt" },
  { value: "faAppleCrate", label: "Apple crate", transKey: "app.foodIconNames.faAppleCrate" },
  { value: "faBacon", label: "Bacon", transKey: "app.foodIconNames.faBacon" },
  { value: "faBeer", label: "Beer", transKey: "app.foodIconNames.faBeer" },
  { value: "faBlender", label: "Blender", transKey: "app.foodIconNames.faBlender" },
  { value: "faBone", label: "Bone", transKey: "app.foodIconNames.faBone" },
  { value: "faBreadLoaf", label: "Bread loaf", transKey: "app.foodIconNames.faBreadLoaf" },
  { value: "faBreadSlice", label: "Bread slice", transKey: "app.foodIconNames.faBreadSlice" },
  { value: "faBurgerSoda", label: "Burger & Soda", transKey: "app.foodIconNames.faBurgerSoda" },
  { value: "faBurrito", label: "Burrito", transKey: "app.foodIconNames.faBurrito" },
  { value: "faCandyCane", label: "Candy Cane", transKey: "app.foodIconNames.faCandyCane" },
  { value: "faCandyCorn", label: "Candy Corn", transKey: "app.foodIconNames.faCandyCorn" },
  { value: "faCarrot", label: "Carrot", transKey: "app.foodIconNames.faCarrot" },
  { value: "faCheese", label: "Cheese", transKey: "app.foodIconNames.faCheese" },
  { value: "faCheeseSwiss", label: "Swiss Cheese", transKey: "app.foodIconNames.faCheeseSwiss" },
  { value: "faCheeseburger", label: "Cheeseburger", transKey: "app.foodIconNames.faCheeseburger" },
  { value: "faCloudMeatball", label: "Cloud Meatball", transKey: "app.foodIconNames.faCloudMeatball" },
  { value: "faCocktail", label: "Cocktail", transKey: "app.foodIconNames.faCocktail" },
  { value: "faCoffee", label: "Coffee", transKey: "app.foodIconNames.faCoffee" },
  { value: "faCoffeePot", label: "Coffee Pot", transKey: "app.foodIconNames.faCoffeePot" },
  { value: "faCoffeeTogo", label: "Coffee Togo", transKey: "app.foodIconNames.faCoffeeTogo" },
  { value: "faCookie", label: "Cookie", transKey: "app.foodIconNames.faCookie" },
  { value: "faCookieBite", label: "Cookie Bite", transKey: "app.foodIconNames.faCookieBite" },
  { value: "faCorn", label: "Corn", transKey: "app.foodIconNames.faCorn" },
  { value: "faCroissant", label: "Croissant", transKey: "app.foodIconNames.faCroissant" },
  { value: "faDrumstick", label: "Drumstick", transKey: "app.foodIconNames.faDrumstick" },
  { value: "faDrumstickBite", label: "DrumstickBite", transKey: "app.foodIconNames.faDrumstickBite" },
  { value: "faEgg", label: "Egg", transKey: "app.foodIconNames.faEgg" },
  { value: "faEggFried", label: "Fried Egg", transKey: "app.foodIconNames.faEggFried" },
  { value: "faFish", label: "Fish", transKey: "app.foodIconNames.faFish" },
  { value: "faFishCooked", label: "Cooked Fish", transKey: "app.foodIconNames.faFishCooked" },
  { value: "faFlask", label: "Flask", transKey: "app.foodIconNames.faFlask" },
  { value: "faFlaskPotion", label: "Flask Potion", transKey: "app.foodIconNames.faFlaskPotion" },
  { value: "faFrenchFries", label: "French Fries", transKey: "app.foodIconNames.faFrenchFries" },
  { value: "faGingerbreadMan", label: "Gingerbread Man", transKey: "app.foodIconNames.faGingerbreadMan" },
  { value: "faGlass", label: "Glass", transKey: "app.foodIconNames.faGlass" },
  { value: "faGlassChampagne", label: "Glass of Champagne", transKey: "app.foodIconNames.faGlassChampagne" },
  { value: "faGlassCheers", label: "Glass / Cheers", transKey: "app.foodIconNames.faGlassCheers" },
  { value: "faGlassCitrus", label: "Glass of Citrus", transKey: "app.foodIconNames.faGlassCitrus" },
  { value: "faGlassMartini", label: "Glass of Martini", transKey: "app.foodIconNames.faGlassMartini" },
  { value: "faGlassMartiniAlt", label: "Glass of Martini Alt", transKey: "app.foodIconNames.faGlassMartiniAlt" },
  { value: "faGlassWhiskey", label: "Glass of Whiskey", transKey: "app.foodIconNames.faGlassWhiskey" },
  {
    value: "faGlassWhiskeyRocks",
    label: "Glass Whiskey on the Rocks",
    transKey: "app.foodIconNames.faGlassWhiskeyRocks",
  },
  { value: "faGlasses", label: "Glasses", transKey: "app.foodIconNames.faGlasses" },
  { value: "faGlassesAlt", label: "Glasses Alt", transKey: "app.foodIconNames.faGlassesAlt" },
  { value: "faHamburger", label: "Hamburger", transKey: "app.foodIconNames.faHamburger" },
  { value: "faHatChef", label: "Hat Chef", transKey: "app.foodIconNames.faHatChef" },
  { value: "faHotdog", label: "Hotdog", transKey: "app.foodIconNames.faHotdog" },
  { value: "faIceCream", label: "Ice Cream", transKey: "app.foodIconNames.faIceCream" },
  { value: "faJug", label: "Jug", transKey: "app.foodIconNames.faJug" },
  { value: "faLemon", label: "Lemon", transKey: "app.foodIconNames.faLemon" },
  { value: "faMeat", label: "Meat", transKey: "app.foodIconNames.faMeat" },
  { value: "faMug", label: "Mug", transKey: "app.foodIconNames.faMug" },
  { value: "faMugHot", label: "Hot Mug", transKey: "app.foodIconNames.faMugHot" },
  { value: "faMugMarshmallows", label: "Mug with Marshmallows", transKey: "app.foodIconNames.faMugMarshmallows" },
  { value: "faMugTea", label: "Cup of Tea", transKey: "app.foodIconNames.faMugTea" },
  { value: "faPepperHot", label: "Hot Pepper", transKey: "app.foodIconNames.faPepperHot" },
  { value: "faPie", label: "Pie", transKey: "app.foodIconNames.faPie" },
  { value: "faPizza", label: "Pizza", transKey: "app.foodIconNames.faPizza" },
  { value: "faPizzaSlice", label: "Pizza Slice", transKey: "app.foodIconNames.faPizzaSlice" },
  { value: "faPopcorn", label: "Popcorn", transKey: "app.foodIconNames.faPopcorn" },
  { value: "faPumpkin", label: "Pumpkin", transKey: "app.foodIconNames.faPumpkin" },
  { value: "faSalad", label: "Salad", transKey: "app.foodIconNames.faSalad" },
  { value: "faSandwich", label: "Sandwich", transKey: "app.foodIconNames.faSandwich" },
  { value: "faSausage", label: "Sausage", transKey: "app.foodIconNames.faSausage" },
  { value: "faSeedling", label: "Seedling", transKey: "app.foodIconNames.faSeedling" },
  { value: "faShishKebab", label: "Shish Kebab", transKey: "app.foodIconNames.faShishKebab" },
  { value: "faSoup", label: "Soup", transKey: "app.foodIconNames.faSoup" },
  { value: "faSteak", label: "Steak", transKey: "app.foodIconNames.faSteak" },
  { value: "faTurkey", label: "Turkey", transKey: "app.foodIconNames.faTurkey" },
  { value: "faWheat", label: "Wheat", transKey: "app.foodIconNames.faWheat" },
  { value: "faWineBottle", label: "Wine Bottle", transKey: "app.foodIconNames.faWineBottle" },
  { value: "faWineGlass", label: "Wine Glass", transKey: "app.foodIconNames.faWineGlass" },
  { value: "faWineGlassAlt", label: "Wine Glass Alt", transKey: "app.foodIconNames.faWineGlassAlt" },
];

/**
 * Lijst met API providers
 */
export const apiProviders: {
  value: string;
  label: string;
  transKey?: string;
  features: Record<string, boolean>;
}[] = [
  {
    value: "JAMEZZ",
    label: "Geen kassa koppeling",
    transKey: "app.apiProviders.JAMEZZ",
    features: { editProductPrice: true, editProductActive: true },
  },
  { value: "MRWINSTON", label: "Mr. Winston", features: {} },
  {
    value: "UNTILL",
    label: "Untill",
    features: { editProductActive: true, arrangements: true, dispatch_message_to_pos: true },
  },
  { value: "VECTRON", label: "Vectron", features: {} },
  { value: "TRIVECLITE", label: "Trivec lite", features: { arrangements: true } },
  { value: "LIGHTSPEED", label: "Lightspeed", features: {} },
  { value: "MPLUS", label: "Mplus", features: { arrangements: true } },
  { value: "NCRALOHA", label: "NCR Aloha", features: { editProductActive: true, arrangements: true } },
  { value: "TWELVE", label: "Twelve", features: {} },
  { value: "LIGHTSPEEDK", label: "Lightspeed K", features: { arrangements: true, dispatch_message_to_pos: true } },
  { value: "CASHDESK", label: "CashDesk", features: {} },
  { value: "GASTROFIX", label: "Gastrofix", features: {} },
  { value: "SIMPHONY", label: "Oracle Simphony2", features: { arrangements: true } },
  { value: "MICROS3700", label: "Micros 3700", features: {} },
  { value: "NUVOPOS", label: "Nuvopos", features: {} },
  { value: "GREATPOS", label: "Greatpos", features: {} },
  { value: "CLUBPLANNER", label: "Clubplanner", features: {} },
  { value: "E-CASH", label: "E-cash", features: {} },
  { value: "TAURUS", label: "Taurus", features: { arrangements: true } },
  { value: "TWEAKWISE", label: "Tweakwise", features: {} },
  { value: "BOOMI", label: "Boomi", features: {} },
  { value: "DELIVERECT", label: "Deliverect", features: {} },
  { value: "EDS", label: "EDS", features: {} },
];

/**
 * Lijst met prices modes
 */
export const priceModes = [
  { value: "default", label: "Default" },
  { value: "eatin", label: "Eat In" },
  { value: "delivery", label: "Delivery" },
  { value: "takeaway", label: "Takeaway" },
];

/**
 * Lijst met thema's
 */
export const activeThemes = [
  { value: "DEFAULT", label: "Default" },
  { value: "black", label: "Black" },
  { value: "orange", label: "Orange" },
  { value: "blauw", label: "Blauw" },
  { value: "rood", label: "Rood" },
  { value: "groen", label: "Groen" },
  { value: "kioskv2boColors", label: "Kiosk v2.1 BackOffice Colors (Kiosk Default)" },
  { value: "kioskv2boColorsRes768x1024", label: "Kiosk v2.1 BackOffice Colors (low res 768x1024)" },
  { value: "kioskv2", label: "Kiosk v2.0" },
  { value: "febo", label: "Febo" },
  { value: "sushito", label: "Kiosk Sushito" },
  { value: "woktogo", label: "Kiosk Wok To Go (tijdelijk)" },
  { value: "jumbo", label: "Kiosk Jumbo" },
];

/**
 * Lijst met Kioskv2 Additional Custom Themes (Options Popup)
 */
export const kioskProductOptionsPopupThemes = [
  { value: 0, label: "Default (1080p)", transKey: "app.kioskProductOptionsPopupThemes.0" },
  { value: 1, label: "Retro (768x1024)", transKey: "app.kioskProductOptionsPopupThemes.1" },
];

/**
 * Lijst met Kioskv2's GridWidths (Options Popup)
 */
export const kioskProductOptionsGridWidths = [
  { value: 3, label: "3 options per row (default)", transKey: "app.'kioskProductOptionsGridWidth's.3" },
  { value: 4, label: "4 options per row", transKey: "app.'kioskProductOptionsGridWidths.4" },
];

/**
 * Lijst met Kioskv2 Additional Custom Cart Button Types
 */
export const kioskCartButtonTypes = [
  { value: 0, label: "Default 2020 (circle)", transKey: "app.kioskCartButtonTypes.0" },
  { value: 1, label: "Default 2021 (bar)", transKey: "app.kioskCartButtonTypes.1" },
];

/**
 * Lijst met Kioskv2 Checkbox Types
 */
export const kioskCheckboxTypes = [
  { value: 0, label: "Default 2020 (Image based checkboxes)", transKey: "app.kioskCheckboxTypes.0" },
  { value: 1, label: "Default 2021 (CCS based checkboxes)", transKey: "app.kioskCheckboxTypes.1" },
];

/**
 * Lijst met Kioskv2 CCS Checkbox Border Types
 */
export const kioskCheckboxBorderTypes = [
  { value: 0, label: "Rounded square", transKey: "app.kioskCheckboxBorderTypes.0" },
  { value: 1, label: "Square", transKey: "app.kioskCheckboxBorderTypes.1" },
  { value: 2, label: "Circle", transKey: "app.kioskCheckboxBorderTypes.2" },
];

/**
 * Lijst met Kioskv2 kioskCustomFields Types
 */
export const kioskCustomFieldsTypes = [
  { value: 0, label: "Above cart-items", transKey: "app.kioskCustomFieldsTypes.0" },
  { value: 1, label: "Below cart-items", transKey: "app.kioskCustomFieldsTypes.1" },
];

/**
 * Print protocols
 */
export const printProtocol = [
  { value: "Cloud", label: "Star cloud printer", transKey: "app.printProtocol.cloudprinter" },
  { value: "Epson", label: "Epson ESC/POS print protocol", transKey: "app.printProtocol.epson" },
  {
    value: "ESC/POS Emulation",
    label: "ESC/POS Emulation (no status check)",
    transKey: "app.printProtocol.escemulation",
  },
  {
    value: "Star",
    label: "Star print protocol (alpha testing)",
    transKey: "app.printProtocol.star",
  },
];

/**
 * Printer types
 */
export const printMode = [
  { value: "default", label: "Default ticket printer", transKey: "app.printMode.default" },
  { value: "wb", label: "Kitchen ticket printer", transKey: "app.printMode.wb" },
];

/**
 * Lijst met Kioskv2 kioskShowVoucherInformation Types
 */
export const kioskShowVoucherInformationTypes = [
  { value: 0, label: "Show voucher messages only", transKey: "app.kioskShowVoucherInformationTypes.0" },
  { value: 1, label: "Show all voucher messages & information", transKey: "app.kioskShowVoucherInformationTypes.1" },
  { value: 2, label: "Hide all voucher messages & information", transKey: "app.kioskShowVoucherInformationTypes.2" },
];

/**
 * Lijst met Kioskv2 instoreScreen Types
 */
export const instoreScreenTypes = [
  { value: 0, label: "Default (Eat in / Takeaway)", transKey: "app.instoreScreenTypes.0" },
  { value: 1, label: "Sales area selection", transKey: "app.instoreScreenTypes.1" },
];

/**
 * Lijst met Kioskv2 instoreMultiSA Orientations
 */
export const instoreMultiSAOrientations = [
  { value: 0, label: "Vertical (Default)", transKey: "app.instoreMultiSAOrientations.0" },
  { value: 1, label: "Horizontal", transKey: "app.instoreMultiSAOrientations.1" },
];

/**
 * Lijst met mogelijke Notification types
 */
export const notificationTypes = [
  { value: "startup", label: "Alleen bij start", transKey: "app.notificationTypes.startup" },
  { value: "direct", label: "Direct", transKey: "app.notificationTypes.direct" },
];

/**
 * Lijst met contant Betalen Types
 */
export const contantBetalenTypes = [
  {
    value: 0,
    label: "as selectable payment method",
    transKey: "app.contantBetalenTypes.0",
  }, //default
  {
    value: 1,
    label: "in customfield dropdown",
    transKey: "app.contantBetalenTypes.1",
  },
];

/**
 * Lijst met contant Betalen Types
 */
export const mollieTransactionModes = [
  {
    value: "ONLINE",
    label: "ONLINE (iDeal, Bancontact, Creditcard, ...)",
    transKey: "app.mollieTransactionModes.online",
  },
  {
    value: "OFFLINE",
    label: "OFFLINE Terminal payments",
    transKey: "app.mollieTransactionModes.offline",
  },
];

export const discountTypes = [
  { value: "AMOUNT_OFF_TOTAL", label: "AMOUNT_OFF_TOTAL" },
  { value: "PERCENTAGE_OFF_TOTAL", label: "PERCENTAGE_OFF_TOTAL" },
  { value: "AMOUNT_OFF_PRODUCT", label: "AMOUNT_OFF_PRODUCT" },
  { value: "PERCENTAGE_OFF_PRODUCT", label: "PERCENTAGE_OFF_PRODUCT" },
  { value: "ADD_DISCOUNTED_PRODUCT", label: "ADD_DISCOUNTED_PRODUCT" },
  { value: "COLLECTION", label: "COLLECTION (Multiple voucher definitions)" },
];

export const customExports = [
  { value: "adofoodexport", label: "Food Export" },
  { value: "adodrinksexport", label: "Drinks Export" },
  { value: "adofooddraaiboek", label: "Draaiboek" },
];

export const taggables = [
  { value: "product", label: "Product tag" },
  { value: "menucard", label: "Menucard tag" },
];

export const vestigingTypes = [
  { value: 1, label: "main", transKey: "app.vestigingTypes.main" },
  { value: 2, label: "copy", transKey: "app.vestigingTypes.copy" },
  { value: 3, label: "part", transKey: "app.vestigingTypes.part" },
  { value: 4, label: "special", transKey: "app.vestigingTypes.special" },
];

export const displayOnScreenGroup = [
  { value: 0, label: "Use default settings (product level)", transKey: "app.displayOnScreenGroup.v0" },
  { value: 1, label: "Display on all kitchen screens", transKey: "app.displayOnScreenGroup.v1" },
  { value: 2, label: "Display on kitchen screen #2", transKey: "app.displayOnScreenGroup.v2" },
  { value: 3, label: "Display on kitchen screen #3", transKey: "app.displayOnScreenGroup.v3" },
  { value: 4, label: "Display on kitchen screen #4", transKey: "app.displayOnScreenGroup.v4" },
  { value: 5, label: "Display on kitchen screen #5", transKey: "app.displayOnScreenGroup.v5" },
  { value: 6, label: "Display on kitchen screen #6", transKey: "app.displayOnScreenGroup.v6" },
  { value: 7, label: "Display on kitchen screen #7", transKey: "app.displayOnScreenGroup.v7" },
  { value: 8, label: "Display on kitchen screen #8", transKey: "app.displayOnScreenGroup.v8" },
  { value: 9, label: "Display on kitchen screen #9", transKey: "app.displayOnScreenGroup.v9" },
  { value: 10, label: "Display on kitchen screen #10", transKey: "app.displayOnScreenGroup.v10" },
  { value: 11, label: "Display on kitchen screen #11", transKey: "app.displayOnScreenGroup.v11" },
  { value: 12, label: "Display on kitchen screen #12", transKey: "app.displayOnScreenGroup.v12" },
  { value: 13, label: "Display on kitchen screen #13", transKey: "app.displayOnScreenGroup.v13" },
  { value: 14, label: "Display on kitchen screen #14", transKey: "app.displayOnScreenGroup.v14" },
  { value: 15, label: "Display on kitchen screen #15", transKey: "app.displayOnScreenGroup.v15" },
  { value: 16, label: "Display on kitchen screen #16", transKey: "app.displayOnScreenGroup.v16" },
  { value: 17, label: "Display on kitchen screen #17", transKey: "app.displayOnScreenGroup.v17" },
  { value: 18, label: "Display on kitchen screen #18", transKey: "app.displayOnScreenGroup.v18" },
  { value: 19, label: "Display on kitchen screen #19", transKey: "app.displayOnScreenGroup.v19" },
  { value: 20, label: "Display on kitchen screen #20", transKey: "app.displayOnScreenGroup.v20" },
];

export const displayOnScreen = [
  { value: 0, label: "Do not display on kitchen screens", transKey: "app.displayOnScreen.v0" },
  { value: 1, label: "Display on all kitchen screens", transKey: "app.displayOnScreen.v1" },
  { value: 2, label: "Display on kitchen screen #2", transKey: "app.displayOnScreen.v2" },
  { value: 3, label: "Display on kitchen screen #3", transKey: "app.displayOnScreen.v3" },
  { value: 4, label: "Display on kitchen screen #4", transKey: "app.displayOnScreen.v4" },
  { value: 5, label: "Display on kitchen screen #5", transKey: "app.displayOnScreen.v5" },
  { value: 6, label: "Display on kitchen screen #6", transKey: "app.displayOnScreen.v6" },
  { value: 7, label: "Display on kitchen screen #7", transKey: "app.displayOnScreen.v7" },
  { value: 8, label: "Display on kitchen screen #8", transKey: "app.displayOnScreen.v8" },
  { value: 9, label: "Display on kitchen screen #9", transKey: "app.displayOnScreen.v9" },
  { value: 10, label: "Display on kitchen screen #10", transKey: "app.displayOnScreen.v10" },
  { value: 11, label: "Display on kitchen screen #11", transKey: "app.displayOnScreen.v11" },
  { value: 12, label: "Display on kitchen screen #12", transKey: "app.displayOnScreen.v12" },
  { value: 13, label: "Display on kitchen screen #13", transKey: "app.displayOnScreen.v13" },
  { value: 14, label: "Display on kitchen screen #14", transKey: "app.displayOnScreen.v14" },
  { value: 15, label: "Display on kitchen screen #15", transKey: "app.displayOnScreen.v15" },
  { value: 16, label: "Display on kitchen screen #16", transKey: "app.displayOnScreen.v16" },
  { value: 17, label: "Display on kitchen screen #17", transKey: "app.displayOnScreen.v17" },
  { value: 18, label: "Display on kitchen screen #18", transKey: "app.displayOnScreen.v18" },
  { value: 19, label: "Display on kitchen screen #19", transKey: "app.displayOnScreen.v19" },
  { value: 20, label: "Display on kitchen screen #20", transKey: "app.displayOnScreen.v20" },
];

export const invoicePeriod = [
  { value: "", label: "Niet van toepassing", transKey: "Niet van toepassing" },
  { value: 1, label: "month", transKey: "app.invoicePeriod.month" },
  { value: 2, label: "year", transKey: "app.invoicePeriod.year" },
  { value: 3, label: "season", transKey: "app.invoicePeriod.season" },
  { value: 4, label: "custom", transKey: "app.invoicePeriod.custom" },
  { value: 5, label: "flex", transKey: "app.invoicePeriod.flex" },
];

export const errorLevels = [
  { value: 0, label: "EMERGENCY", color: "warning" },
  { value: 1, label: "ALERT", color: "warning" },
  { value: 2, label: "CRITICAL", color: "danger" },
  { value: 3, label: "ERROR", color: "danger" },
  { value: 4, label: "WARNING", color: "warning" },
  { value: 5, label: "NOTICE", color: "info" },
  { value: 6, label: "INFO", color: "info" },
  { value: 7, label: "DEBUG", color: "info" },
];

export const runningRelease = [
  { value: "https://jamezz.app/", label: "Stable", color: "warning" },
  { value: "https://rc.jamezz.app/", label: "Release Candidate", color: "warning" },
  { value: "https://staging.jamezz.app/", label: "Staging", color: "danger" },
  { value: "https://nprc.jamezz.app/", label: "NPRC", color: "danger" },
  { value: "https://alpha.jamezz.app/", label: "Alpha", color: "danger" },
  { value: "http://jamezzv2.test/", label: "Development", color: "danger" },
  { value: "https://jamezzv2.test/", label: "Development (SSL)", color: "danger" },
];

// IMPORTANT: Database column has max length 24
export const dispatchQueue = [
  { value: "", label: "Default", color: "" },
  { value: "dispatch_queue_1", label: "Dispatch Queue 1", color: "" },
  { value: "dispatch_queue_2", label: "Dispatch Queue 2", color: "" },
  { value: "dispatch_queue_3", label: "Dispatch Queue 3", color: "" },
];

export const valuta = [
  { value: "EUR", label: "Euro", symbol: "€" },
  { value: "CHF", label: "Zwitserse frank", symbol: "CHF" },
  { value: "GBP", label: "Britse pond", symbol: "£" },
  { value: "ANG", label: "Antilliaanse gulden", symbol: "ANG" },
  { value: "USD", label: "Amerikaanse dollar", symbol: "$" },
  { value: "NOK", label: "Noorse kroon", symbol: "kr" },
  { value: "SEK", label: "Zweedse kroon", symbol: "kr" },
  { value: "DKK", label: "Deense kroon", symbol: "kr" },
];

export const salesareaTypes = [
  { label: "QR", value: "QR" },
  { label: "PICKUP_DELIVERY", value: "PICKUP_DELIVERY" },
  { label: "KIOSK", value: "KIOSK" },
  { label: "CATALOG", value: "CATALOG" },
  { label: "TABLET", value: "TABLET" },
];

export const paymentMethods = [
  { value: "PIN" },
  { value: "ALIPAY" },
  { value: "WECHAT" },
  { value: "PIGGY_PREPAID.PREPAID", label: "Piggy Prepaid" },
];

export const appPrintMode = [
  { value: "WEBSOCKETS", label: "Use websockets" },
  { value: "POLLING", label: "Use polling" },
];

export const orderDateSendToApiModes = [
  { value: 0, label: "moment of ordering (default)", transKey: "app.vestigingTypes.main" },
  { value: 1, label: "moment of dispatching to api", transKey: "app.vestigingTypes.copy" },
  {
    value: 2,
    label: "preorder target moment (date time of show) (if applicable)",
    transKey: "app.vestigingTypes.part",
  },
];

export function apiProviderSupportsFeature(name: string, feature: string) {
  name = name ?? "JAMEZZ";
  const aliases: Record<string, string> = {
    BORK: "TRIVECLITE",
  };
  if (name in aliases) {
    name = aliases[name];
  }
  const provider = apiProviders.find((provider) => provider.value === name);
  if (!provider) {
    throw new Error(`Provider ${name} not found in 'apiProviders' and has no alias.`);
  }
  return provider?.features[feature] ?? false;
}
export function apiProviderSupportsFeatureList(feature: string) {
  return apiProviders.filter((apiProvider) => apiProvider.features[feature]);
}
